import { useEffect, useState } from "react";
import { ShieldCheck, Award, CheckCircle, Play } from "lucide-react";
import { fetchPages } from "../lib/cms";
import { videoer } from "../data";
import VideoModal from "../components/VideoModal";

export default function OmMig() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    async function loadData() {
      const pages = await fetchPages();
      const omMigPage = pages.find((p) => p.page_slug === "om-mig");
      setPageData(omMigPage);
      setLoading(false);
    }
    loadData();
  }, []);

  // Sæt sidetitel og meta-beskrivelse
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || "Om mig - RG Fodpleje";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "Læs mere om Rikke og klinikken RG Fodpleje.");
      }
    }
  }, [pageData]);

  return (
    <div className="bg-[#FAF8F5] text-[#2D2A26] min-h-screen py-12 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hovedindhold: Tekst og Billede */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
          
          {/* Venstre side: Billede */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-md border-4 border-white bg-stone-100 aspect-[3/4] max-w-md mx-auto">
              <img
                src="https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/1b0e52f5-3c4b-4fbb-8990-a60b7cc063da.JPEG" // IMG_0029.JPEG
                alt="RG Fodpleje Klinik"
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-[#4A5D4E]/90 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                Sunde fødder i trygge hænder
              </div>
            </div>
          </div>

          {/* Højre side: Tekst */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A5D4E] uppercase tracking-wider bg-[#E8EFEA] px-3 py-1.5 rounded-full">
              <Award className="h-4 w-4" />
              <span>Uddannet Fodplejer</span>
            </div>
            
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2D2A26]">
              Om mig
            </h1>

            {/* Brødtekst (bevarer stavefejl verbatim) */}
            {pageData && (
              <div
                className="text-[#6B6661] font-light leading-relaxed space-y-6 prose prose-stone max-w-none"
                dangerouslySetInnerHTML={{ __html: pageData.content }}
              />
            )}
          </div>

        </div>

        {/* Sektion: Faglig stolthed og hygiejne */}
        <div className="bg-[#F3EFEA] rounded-3xl p-8 sm:p-12 border border-[#EAE5DF] grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Venstre side: Tekst */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A5D4E] uppercase tracking-wider">
              <ShieldCheck className="h-4 w-4" />
              <span>Din Tryghedsgaranti</span>
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2D2A26]">
              Medlem af SADF
            </h2>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Sammenslutningen Af Dansk Fodplejere (SADF) er din garanti for, at jeg har gennemgået en godkendt og professionel uddannelse, samt at klinikken lever op til strenge krav vedrørende hygiejne, sterilisering af instrumenter og journalføring.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="flex items-center gap-2 text-sm text-[#2D2A26]">
                <CheckCircle className="h-5 w-5 text-[#4A5D4E] shrink-0" />
                <span>Autoklave-steriliseret værktøj</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#2D2A26]">
                <CheckCircle className="h-5 w-5 text-[#4A5D4E] shrink-0" />
                <span>Forseglede poser til hvert brug</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#2D2A26]">
                <CheckCircle className="h-5 w-5 text-[#4A5D4E] shrink-0" />
                <span>Branchegodkendt klinik</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#2D2A26]">
                <CheckCircle className="h-5 w-5 text-[#4A5D4E] shrink-0" />
                <span>Løbende efteruddannelse</span>
              </div>
            </div>
          </div>

          {/* Højre side: Video */}
          <div className="lg:col-span-5">
            <div 
              onClick={() => setIsModalOpen(true)}
              className="relative rounded-2xl overflow-hidden shadow-lg border-4 border-white bg-stone-100 aspect-video cursor-pointer group"
            >
              <video
                id="about-video"
                src={videoer[1].url} // RG Fodpleje video 4.mp4
                poster="https://assets.auxodev.dk/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/1b0e52f5-3c4b-4fbb-8990-a60b7cc063da.JPEG" // IMG_0029.JPEG
                className="h-full w-full object-cover"
                autoPlay
                loop
                muted
                playsInline
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                <div
                  className="h-14 w-14 rounded-full bg-[#FAF8F5] group-hover:bg-white text-[#4A5D4E] flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110"
                >
                  <Play className="h-5 w-5 fill-current ml-0.5" />
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-[#8B857F] mt-3 italic">
              Klinikkens sterile instrumenter og autoklave i brug
            </p>
          </div>

        </div>

      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        videoUrl={videoer[1].url}
        title="Klinikkens sterile instrumenter og autoklave i brug - RG Fodpleje"
      />
    </div>
  );
}
